// Hilo de Benito persistido entre sesiones — "seguir donde quedaste". Distinto
// de companero_memoria (hechos del usuario, en BD): esto es la conversación
// misma, liviana y descartable, así que basta localStorage (no vale un viaje
// a Supabase por esto, y así funciona igual sin conexión).
//
// Cap de mensajes: sin tope, una sesión larga de turno podría dejar el hilo
// creciendo indefinidamente en localStorage. Se filtran los mensajes "paso"
// (los "Buscando…"/"Anotando…" efímeros del loop) porque no aportan nada al
// releer la conversación en la próxima sesión.

const CAP_DISPLAY = 20;
export const CAP_CONV = 12;

// Recorte de la conversación VIVA (la que se re-manda a la API en cada envío),
// no solo la que se persiste. Kill-switch ON por defecto.
//
// El tope de 12 existía desde siempre pero solo mordía al guardar y al cargar:
// `convRef` en CompaneroGlobal acumulaba el array completo del loop y lo
// re-mandaba entero. Como el panel vive fuera del router lazy (en PWA y nativo
// la pestaña dura días sin recargar) y "Nueva conversación" se usó 35 veces
// contra 3.458 mensajes en 30 días, en la práctica el tope casi nunca se
// aplicaba donde cuesta plata. Medido en producción el 2026-09-03: el 21% de
// las llamadas de Benito mandaba prompts sobre 20.000 tokens y una sola
// conversación llegó a 121.557, con noches de US$0,24 a US$0,28 por mensaje.
// O sea la app ya declaraba y ya ejecutaba este tope: lo que faltaba era
// aplicarlo también en vivo.
const CONV_CAP_VIVO = process.env.REACT_APP_BENITO_CONV_CAP_ENABLED !== 'false';

// MARCA DE AGUA: se recorta solo al pasar 2xCAP, no en cada turno. No es un
// detalle de eficiencia, es lo que decide el signo del cambio: recortar en
// CADA turno correría la ventana dos posiciones por vuelta, así que el prefijo
// de `messages` cambiaría en cada request y la caché de mensajes moriría —
// pasaría de leer a 0.1x a re-escribir la ventana entera a 1.25x SIEMPRE, que
// sale más caro que el problema que arregla. Con la marca de agua el prefijo
// queda estable unos seis intercambios entre recortes.
const MARCA_AGUA = 2 * CAP_CONV;

/**
 * Recorta la conversación viva conservando el PRELUDIO al frente.
 *
 * El preludio (contexto de capa, memoria del usuario y bloque [Ahora]) se
 * inyecta UNA sola vez, cuando la conversación está vacía, y vive al INICIO
 * del array — justo lo que un recorte por la cola se llevaría, y sin volver a
 * inyectarse nunca (la longitud ya no es 0). Perderlo deja a Benito sin saber
 * si le habla a una estudiante o a una enfermera de turno y sin la memoria que
 * la usuaria escribió a mano: el ahorro se cobraría en calidad clínica, que es
 * exactamente lo que no se hace.
 *
 * Pura y determinista. `preludio` son los mensajes a fijar al frente (puede ir
 * vacío). Devuelve `conv` intacto mientras no se cruce la marca de agua.
 */
export function recortarConvViva(conv, preludio = []) {
  const arr = Array.isArray(conv) ? conv : [];
  if (!CONV_CAP_VIVO || arr.length <= MARCA_AGUA) return arr;
  const fijo = Array.isArray(preludio) ? preludio : [];
  // La cola se recorta con la MISMA función de siempre (incluida la regla de
  // arranque limpio que evita el tool_result huérfano y el 400 de #645).
  const cola = recortarConv(arr.slice(fijo.length));
  // Si la cola quedó sin arranque limpio, `recortarConv` devuelve []; el
  // preludio solo sobrevive acompañado, porque una conversación que empieza y
  // termina en el preludio no tiene nada que continuar.
  return cola.length ? [...fijo, ...cola] : cola;
}

function clave(userId) {
  return `mipae_benito_hilo_${userId}`;
}

// Un mensaje user "limpio" (texto, no tool_result huérfano): el único punto
// donde puede EMPEZAR una conversación recortada. Cortar en medio de un par
// tool_use/tool_result dejaba un conv que la API rechaza (400) en CADA envío
// tras restaurar el hilo (barrido 2026-08-02).
function esUserLimpio(m) {
  if (!m || m.role !== 'user') return false;
  if (typeof m.content === 'string') return true;
  return Array.isArray(m.content) && m.content.every((b) => b && b.type === 'text');
}

export function recortarConv(conv) {
  const corte = (Array.isArray(conv) ? conv : []).slice(-CAP_CONV);
  const inicio = corte.findIndex(esUserLimpio);
  // Sin arranque limpio, mejor conv vacío (el display persiste igual y la
  // charla re-parte con contexto fresco) que un hilo que revienta la API.
  return inicio < 0 ? [] : corte.slice(inicio);
}

function limpiarDisplay(display) {
  return (Array.isArray(display) ? display : [])
    .filter((m) => m && m.rol !== "paso")
    .slice(-CAP_DISPLAY);
}

// Guarda el hilo. Envuelto en try/catch: localStorage puede fallar (modo
// privado, cuota llena) y eso nunca debe romper la conversación en curso.
export function guardarHilo(userId, { display, conv } = {}, ts = Date.now()) {
  if (!userId) return;
  try {
    const payload = {
      ts,
      display: limpiarDisplay(display),
      conv: recortarConv(conv),
    };
    localStorage.setItem(clave(userId), JSON.stringify(payload));
  } catch {
    /* localStorage no disponible o lleno: el hilo simplemente no persiste */
  }
}

// Carga el hilo guardado. Devuelve null si no hay, si está corrupto o si
// falla el acceso a localStorage — nunca lanza (el caller decide el fallback).
export function cargarHilo(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(clave(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.display) || !Array.isArray(parsed.conv)) return null;
    // Sana hilos guardados por versiones viejas (el recorte podía dejar un
    // tool_result huérfano al inicio): misma regla al leer que al escribir.
    return { ...parsed, conv: recortarConv(parsed.conv) };
  } catch {
    return null;
  }
}

// Borra el hilo guardado (botón "Nueva conversación").
export function limpiarHilo(userId) {
  if (!userId) return;
  try { localStorage.removeItem(clave(userId)); } catch { /* noop */ }
}

// Un hilo vive 12 h: dentro de ese plazo el panel lo restaura entero; pasado,
// se ofrece retomar su tema. Fuente única para el panel y para el inicio.
export const VIGENCIA_HILO_MS = 12 * 60 * 60 * 1000;

export function hiloVigente(hilo, ahora = Date.now()) {
  return !!hilo && Array.isArray(hilo.display) && hilo.display.length > 0
    && (ahora - (hilo.ts || 0)) < VIGENCIA_HILO_MS;
}

// El tema para "Seguir donde quedamos": solo de un hilo que ya venció. Uno
// vigente se restaura entero al abrir el panel, así que no hay nada que retomar.
export function temaParaRetomar(hilo, ahora = Date.now()) {
  if (!hilo || hiloVigente(hilo, ahora)) return null;
  return temaDeHilo(hilo);
}

// Tema de un hilo: el último mensaje del USUARIO, recortado a una línea corta.
// Cuando el hilo ya venció (>12 h no se restaura entero), esto permite ofrecer
// "seguir donde quedamos" como chip — el gesto de un compañero que recuerda.
// Se queda en el dispositivo (el hilo ya vive ahí) y solo viaja al modelo si
// el usuario TOCA el chip: retomar es siempre decisión suya.
export function temaDeHilo(hilo, maxLargo = 60) {
  if (!hilo || !Array.isArray(hilo.display)) return null;
  const ultimo = [...hilo.display]
    .reverse()
    .find((m) => m && m.rol === 'user' && typeof m.texto === 'string' && m.texto.trim());
  if (!ultimo) return null;
  const texto = ultimo.texto.trim().replace(/\s+/g, ' ');
  if (texto.length <= maxLargo) return texto;
  return `${texto.slice(0, maxLargo - 1).trimEnd()}…`;
}
